import cn from 'classnames';
import {ChangeEvent, DragEvent, FC, useId, useRef, useState} from 'react';
import {PhotoRemoveIcon} from 'shared/icons';
import {isPassportPhotoDataTransfer, isPassportPhotoFile, PASSPORT_PHOTO_ACCEPT} from '../../../model';
import styles from './PassportPhotoUpload.module.css';

type Props = {
	label?: string;
	previewUrl: string | null;
	onSelect: (file: File) => void;
	onRemove: () => void;
	/** Квадратная кнопка «+» без подсказки — как на экране паспортных данных. */
	compact?: boolean;
};

export const PassportPhotoUpload: FC<Props> = ({
	label,
	previewUrl,
	onSelect,
	onRemove,
	compact = false,
}) => {
	const inputId = useId();
	const dragDepthRef = useRef(0);
	const [isDragOver, setIsDragOver] = useState(false);

	const selectFile = (file: File | undefined) => {
		if (!file || !isPassportPhotoFile(file)) {
			return;
		}

		onSelect(file);
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		selectFile(event.target.files?.[0]);
		event.target.value = '';
	};

	const resetDragState = () => {
		dragDepthRef.current = 0;
		setIsDragOver(false);
	};

	const handleDragEnter = (event: DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		event.stopPropagation();

		const isAllowed = isPassportPhotoDataTransfer(event.dataTransfer);
		event.dataTransfer.dropEffect = isAllowed ? 'copy' : 'none';

		dragDepthRef.current += 1;

		if (isAllowed) {
			setIsDragOver(true);
		}
	};

	const handleDragLeave = (event: DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		event.stopPropagation();
		dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

		if (dragDepthRef.current === 0) {
			setIsDragOver(false);
		}
	};

	const handleDragOver = (event: DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		event.stopPropagation();

		const isAllowed = isPassportPhotoDataTransfer(event.dataTransfer);
		event.dataTransfer.dropEffect = isAllowed ? 'copy' : 'none';

		if (!isAllowed) {
			setIsDragOver(false);
		}
	};

	const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		event.stopPropagation();
		resetDragState();

		if (!isPassportPhotoDataTransfer(event.dataTransfer)) {
			return;
		}

		selectFile(event.dataTransfer.files[0]);
	};

	return (
		<div className={styles.root}>
			{label ? <span className={styles.label}>{label}</span> : null}

			{previewUrl ? (
				<div className={cn(styles.previewWrap, compact && styles.previewWrapCompact)}>
					<img className={styles.preview} src={previewUrl} alt="" />
					<button type="button" className={styles.removeButton} aria-label="Удалить фото" onClick={onRemove}>
						<PhotoRemoveIcon />
					</button>
				</div>
			) : (
				<label
					htmlFor={inputId}
					className={cn(
						styles.zone,
						compact && styles.zoneCompact,
						isDragOver && styles.zoneDragOver,
					)}
					onDragEnter={handleDragEnter}
					onDragLeave={handleDragLeave}
					onDragOver={handleDragOver}
					onDrop={handleDrop}
				>
					<span className={styles.plusIcon} aria-hidden>
						+
					</span>
					{compact ? null : (
						<span className={styles.hint}>Загрузите фото в формате jpeg или png</span>
					)}
				</label>
			)}

			<input
				id={inputId}
				className={styles.input}
				type="file"
				accept={PASSPORT_PHOTO_ACCEPT}
				onChange={handleChange}
			/>
		</div>
	);
};
